import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Segment,
    Header,
    Button,
    Table,
    Icon,
    Input,
    Dropdown,
    Pagination,
    Loader,
    Message,
    Checkbox,
    Form,
} from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import {
    User,
    PledgeClass,
    Family,
    GetUsers,
    GetPledgeClasses,
    GetFamilies,
} from "../Utils/UserAPI";
import { LoginInfo } from "../Utils/Login";

interface UserListProps {
    loginInfo: LoginInfo;
}

export default function UserList({ loginInfo }: UserListProps) {
    const history = useHistory();
    const [users, setUsers] = useState<User[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
    const [pledgeClasses, setPledgeClasses] = useState<PledgeClass[]>([]);
    const [families, setFamilies] = useState<Family[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(25);

    // Filter state
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPledgeClass, setSelectedPledgeClass] = useState<number>(0);
    const [selectedFamily, setSelectedFamily] = useState<number>(0);
    const [cityFilter, setCityFilter] = useState("");
    const [stateFilter, setStateFilter] = useState("");
    const [showInMemoriam, setShowInMemoriam] = useState(false);
    const [showNonCorporationMembers, setShowNonCorporationMembers] = useState(false);
    const [showMissingEmail, setShowMissingEmail] = useState(false);
    const [showMissingAddress, setShowMissingAddress] = useState(false);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [canCreate, setCanCreate] = useState(false);
    const [canEditUsers, setCanEditUsers] = useState(false);

    // Sorting state
    const [sortField, setSortField] = useState<string>("");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        // Load all data in parallel
        Promise.all([
            new Promise<void>((resolve) => {
                GetUsers((statusCode, data, canCreateUsers) => {
                    if (statusCode === 200) {
                        setUsers(data);
                        setCanCreate(canCreateUsers || false);
                        setCanEditUsers(canCreateUsers || false); // If they can create, they can edit
                    } else {
                        setError("Failed to load users");
                    }
                    resolve();
                });
            }),
            new Promise<void>((resolve) => {
                GetPledgeClasses((statusCode, data) => {
                    if (statusCode === 200) {
                        setPledgeClasses(data);
                    }
                    resolve();
                });
            }),
            new Promise<void>((resolve) => {
                GetFamilies((statusCode, data) => {
                    if (statusCode === 200) {
                        setFamilies(data);
                    }
                    resolve();
                });
            }),
        ]).finally(() => {
            setLoading(false);
        });
    };

    // Helper functions to get pledge class and family names
    const getPledgeClassName = useCallback((user: User) => {
        if (user.pledge_class) {
            return `${user.pledge_class.name} (${user.pledge_class.pledge_year})`;
        }
        if (user.pledge_class_id) {
            const pledgeClass = pledgeClasses.find(pc => pc.id === user.pledge_class_id);
            if (pledgeClass) {
                return pledgeClass.name;
            }
        }
        return "None";
    }, [pledgeClasses]);

    const sortUsers = useCallback((users: User[], field: string, direction: "asc" | "desc") => {
        if (!field) return users;

        return [...users].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (field) {
                case "name":
                    aValue = `${a.last_name} ${a.first_name}`.toLowerCase();
                    bValue = `${b.last_name} ${b.first_name}`.toLowerCase();
                    break;
                case "year":
                    aValue = a.year || 0;
                    bValue = b.year || 0;
                    break;
                case "chapter":
                    aValue = (a.chapter || "").toLowerCase();
                    bValue = (b.chapter || "").toLowerCase();
                    break;
                case "pledge_class":
                    aValue = getPledgeClassName(a).toLowerCase();
                    bValue = getPledgeClassName(b).toLowerCase();
                    break;
                case "location":
                    const aLocation = a.city && a.state ? `${a.city}, ${a.state}` : a.city || a.state || "";
                    const bLocation = b.city && b.state ? `${b.city}, ${b.state}` : b.city || b.state || "";
                    aValue = aLocation.toLowerCase();
                    bValue = bLocation.toLowerCase();
                    break;
                case "company":
                    aValue = (a.company || "").toLowerCase();
                    bValue = (b.company || "").toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return direction === "asc" ? -1 : 1;
            if (aValue > bValue) return direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [getPledgeClassName]);

    const applyFilters = useCallback(() => {
        let filtered = [...users];

        // Apply search term filter
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            const lc = (s?: string | null) => (s || "").toLowerCase();
            filtered = filtered.filter((user) =>
                lc(user.first_name).includes(q) ||
                lc(user.last_name).includes(q) ||
                lc(user.email).includes(q) ||
                lc(user.company).includes(q)
            );
        }

        // Apply pledge class filter
        if (selectedPledgeClass && selectedPledgeClass > 0) {
            filtered = filtered.filter((user) => user.pledge_class_id === selectedPledgeClass);
        }

        // Apply family filter
        if (selectedFamily && selectedFamily > 0) {
            filtered = filtered.filter((user) => user.family_id === selectedFamily);
        }

        // Apply city filter
        if (cityFilter) {
            const cq = cityFilter.toLowerCase();
            filtered = filtered.filter((user) => (user.city || "").toLowerCase().includes(cq));
        }

        // Apply state filter
        if (stateFilter) {
            const sq = stateFilter.toLowerCase();
            filtered = filtered.filter((user) => (user.state || "").toLowerCase().includes(sq));
        }

        // Apply In Memoriam filter
        if (showInMemoriam) {
            filtered = filtered.filter((user) => user.type === "deceased");
        }

        // Apply non-corporation members filter (only for user admins)
        if (showNonCorporationMembers) {
            filtered = filtered.filter((user) => !user.is_corporation_member);
        }

        // Apply missing email filter (excludes exiled/deceased members —
        // they're not who we're trying to track down)
        if (showMissingEmail) {
            filtered = filtered.filter(
                (user) => user.missing_email && user.type !== "exile" && user.type !== "deceased"
            );
        }

        // Apply missing address filter (excludes exiled/deceased members —
        // they're not who we're trying to track down)
        if (showMissingAddress) {
            filtered = filtered.filter(
                (user) => user.missing_address && user.type !== "exile" && user.type !== "deceased"
            );
        }

        // Apply sorting
        filtered = sortUsers(filtered, sortField, sortDirection);

        setFilteredUsers(filtered);
        setCurrentPage(1); // Reset to first page when filters change
    }, [users, searchTerm, selectedPledgeClass, selectedFamily, cityFilter, stateFilter, showInMemoriam, showNonCorporationMembers, showMissingEmail, showMissingAddress, sortField, sortDirection, sortUsers]);

    // Apply filters whenever dependencies change
    useEffect(() => {
        applyFilters();
    }, [applyFilters]);

    const handleEditUser = (userId: number) => {
        history.push(`/users/${userId}/edit`);
    };

    const handleViewUser = (userId: number) => {
        history.push(`/users/${userId}`);
    };

    const handleCreateUser = () => {
        history.push("/users/create");
    };

    const handleSort = (field: string) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            // Set new field with ascending direction
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const clearFilters = () => {
        setSearchTerm("");
        setSelectedPledgeClass(0);
        setSelectedFamily(0);
        setCityFilter("");
        setStateFilter("");
        setShowInMemoriam(false);
        setShowNonCorporationMembers(false);
        setShowMissingEmail(false);
        setShowMissingAddress(false);
        setSortField("");
        setSortDirection("asc");
    };

    const downloadCSV = () => {
        // Create CSV header
        const headers = [
            "First Name",
            "Last Name",
            "Class Year",
            "Chapter",
            "Pledge Class",
            "Address Line 1",
            "Address Line 2",
            "City",
            "State",
            "ZIP Code",
            "Country",
            "Company",
            "Email",
            "Type",
            "Phone (Cell)",
            "Phone (Work)",
            "Phone (Home)",
        ];

        // Create CSV rows
        const rows = filteredUsers.map((user) => {
            const pledgeClassName = getPledgeClassName(user);
            return [
                user.first_name || "",
                user.last_name || "",
                user.year || "",
                user.chapter || "",
                pledgeClassName,
                user.address_line_1 || "",
                user.address_line_2 || "",
                user.city || "",
                user.state || "",
                user.zip || "",
                user.country || "",
                user.company || "",
                user.email || "",
                user.type || "",
                user.cell_phone || "",
                user.work_phone || "",
                user.home_phone || "",
            ];
        });

        // Escape CSV values (handle commas, quotes, newlines)
        const escapeCSV = (value: string | number): string => {
            const strValue = String(value);
            if (strValue.includes(",") || strValue.includes('"') || strValue.includes("\n")) {
                return `"${strValue.replace(/"/g, '""')}"`;
            }
            return strValue;
        };

        // Combine headers and rows
        const csvContent = [
            headers.map(escapeCSV).join(","),
            ...rows.map((row) => row.map(escapeCSV).join(",")),
        ].join("\n");

        // Create blob and download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `lambda_phi_users_${new Date().toISOString().split("T")[0]}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    // Calculate pagination
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentUsers = filteredUsers.slice(startIndex, endIndex);

    if (loading) {
        return (
            <Container>
                <Loader active size="large">
                    Loading users...
                </Loader>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Message negative>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                    <Button onClick={loadData}>Retry</Button>
                </Message>
            </Container>
        );
    }

    return (
        <Container>
            <Header as="h2">
                <Icon name="users" />
                <Header.Content>
                    Lambda Phi Member Database
                    <Header.Subheader>
                        Search, view, and manage Lambda Phi member database • {filteredUsers.length} of {users.length} users
                    </Header.Subheader>
                </Header.Content>
            </Header>

            <Segment>
                <Header as="h3">Search & Filters</Header>

                {/* Basic Search */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem", alignItems: "flex-end", marginBottom: "1rem" }}>
                    <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
                        <label>Quick Search</label>
                        <Input
                            placeholder="Search by name, email, or company..."
                            value={searchTerm}
                            onChange={(e, { value }) => setSearchTerm(value)}
                            icon="search"
                            fluid
                        />
                    </div>
                    <Button onClick={clearFilters}>Clear All</Button>
                    <Button
                        icon={showAdvancedFilters ? "chevron up" : "chevron down"}
                        onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    >
                        {showAdvancedFilters ? "Hide" : "Show"} Advanced Filters
                    </Button>
                </div>

                {/* Advanced Filters */}
                {showAdvancedFilters && (
                    <div style={{
                        marginBottom: "1rem",
                        padding: "1rem",
                        backgroundColor: "#f9f9f9",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0"
                    }}>
                        <Form>
                            <Form.Group widths="equal">
                                <Form.Field>
                                    <label>Pledge Class</label>
                                    <Dropdown
                                        placeholder="All Pledge Classes"
                                        clearable
                                        selection
                                        options={[
                                            { key: 0, text: "All Pledge Classes", value: 0 },
                                            ...pledgeClasses.map((pc) => ({
                                                key: pc.id,
                                                text: `${pc.name} (${pc.pledge_year})`,
                                                value: pc.id,
                                            }))
                                        ]}
                                        value={selectedPledgeClass || 0}
                                        onChange={(e, { value }) => setSelectedPledgeClass(value as number || 0)}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <label>Family</label>
                                    <Dropdown
                                        placeholder="All Families"
                                        clearable
                                        selection
                                        options={[
                                            { key: 0, text: "All Families", value: 0 },
                                            ...families.map((f) => ({
                                                key: f.id,
                                                text: f.name,
                                                value: f.id,
                                            }))
                                        ]}
                                        value={selectedFamily || 0}
                                        onChange={(e, { value }) => setSelectedFamily(value as number || 0)}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <label>City</label>
                                    <Input
                                        placeholder="Filter by city..."
                                        value={cityFilter}
                                        onChange={(e, { value }) => setCityFilter(value)}
                                    />
                                </Form.Field>
                                <Form.Field>
                                    <label>State</label>
                                    <Input
                                        placeholder="Filter by state..."
                                        value={stateFilter}
                                        onChange={(e, { value }) => setStateFilter(value)}
                                    />
                                </Form.Field>
                            </Form.Group>
                            <Form.Field>
                                <Checkbox
                                    label="In Memoriam"
                                    checked={showInMemoriam}
                                    onChange={(e, { checked }) => setShowInMemoriam(checked || false)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Checkbox
                                    label="Missing Email"
                                    checked={showMissingEmail}
                                    onChange={(e, { checked }) => setShowMissingEmail(checked || false)}
                                />
                            </Form.Field>
                            <Form.Field>
                                <Checkbox
                                    label="Missing Address"
                                    checked={showMissingAddress}
                                    onChange={(e, { checked }) => setShowMissingAddress(checked || false)}
                                />
                            </Form.Field>
                            {canEditUsers && (
                                <Form.Field>
                                    <Checkbox
                                        label="Show Only Non-Corporation Members"
                                        checked={showNonCorporationMembers}
                                        onChange={(e, { checked }) => setShowNonCorporationMembers(checked || false)}
                                    />
                                </Form.Field>
                            )}
                        </Form>
                    </div>
                )}

                {canCreate && (
                    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
                        <Button onClick={handleCreateUser}>
                            <Icon name="add user" />
                            Create New User
                        </Button>
                    </div>
                )}
            </Segment>

            <Segment>
                {canEditUsers && 
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>

                        <Button onClick={downloadCSV} icon labelPosition="right">
                            <Icon name="download" />
                            Download CSV
                        </Button>

                    </div>
                }
                <Table celled>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("name")}
                            >
                                Name
                                {sortField === "name" && (
                                    <Icon name={sortDirection === "asc" ? "sort up" : "sort down"} />
                                )}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("year")}
                            >
                                Class Year
                                {sortField === "year" && (
                                    <Icon name={sortDirection === "asc" ? "sort up" : "sort down"} />
                                )}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("chapter")}
                            >
                                Chapter
                                {sortField === "chapter" && (
                                    <Icon name={sortDirection === "asc" ? "sort up" : "sort down"} />
                                )}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("pledge_class")}
                            >
                                Pledge Class
                                {sortField === "pledge_class" && (
                                    <Icon name={sortDirection === "asc" ? "sort up" : "sort down"} />
                                )}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("location")}
                            >
                                Location
                                {sortField === "location" && (
                                    <Icon name={sortDirection === "asc" ? "sort up" : "sort down"} />
                                )}
                            </Table.HeaderCell>
                            <Table.HeaderCell
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSort("company")}
                            >
                                Company
                                {sortField === "company" && (
                                    <Icon name={sortDirection === "asc" ? "sort up" : "sort down"} />
                                )}
                            </Table.HeaderCell>
                            <Table.HeaderCell>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {currentUsers.map((user) => (
                            <Table.Row key={user.id}>
                                <Table.Cell>
                                    <strong>
                                        {user.first_name} {user.last_name}
                                    </strong>
                                    {user.type === "deceased" && (
                                        <div style={{ fontSize: "0.8em", color: "#666", fontStyle: "italic" }}>
                                            deceased
                                        </div>
                                    )}
                                </Table.Cell>
                                <Table.Cell>{user.year}</Table.Cell>
                                <Table.Cell>{user.chapter}</Table.Cell>
                                <Table.Cell>
                                    {getPledgeClassName(user)}
                                </Table.Cell>
                                <Table.Cell>
                                    {user.city && user.state ? `${user.city}, ${user.state}` : user.city || user.state || "—"}
                                </Table.Cell>
                                <Table.Cell>{user.company || "—"}</Table.Cell>
                                <Table.Cell>
                                    <Button.Group size="mini">
                                        <Button onClick={() => handleViewUser(user.id)}>
                                            <Icon name="eye" />
                                            View
                                        </Button>
                                        {canEditUsers && (
                                            <Button primary onClick={() => handleEditUser(user.id)}>
                                                <Icon name="edit" />
                                                Edit
                                            </Button>
                                        )}
                                    </Button.Group>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>

                {totalPages > 1 && (
                    <div style={{ textAlign: "center", marginTop: "1rem" }}>
                        <Pagination
                            activePage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(e, { activePage }) => setCurrentPage(activePage as number)}
                            firstItem={{ content: <Icon name="angle double left" />, icon: true }}
                            lastItem={{ content: <Icon name="angle double right" />, icon: true }}
                            prevItem={{ content: <Icon name="angle left" />, icon: true }}
                            nextItem={{ content: <Icon name="angle right" />, icon: true }}
                        />
                    </div>
                )}
            </Segment>
        </Container>
    );
}
